import { ServerNotificationsContext } from "@/providers/ServerNotificationsProvider";
import { useContext, useEffect, useRef } from "react";

export const useServerNotifications = () => {
    const context = useContext(ServerNotificationsContext);

    if (!context) {
        throw new Error("useServerNotifications must be used within a ServerNotificationsProvider");
    }

    return context;
};

/**
 * Hook to subscribe to a specific server notification event type
 * Automatically handles cleanup when the component unmounts
 * 
 * @param eventType - The type of event to listen for
 * @param callback - Function to call when the event is received
 * @param dependencies - Dependencies array for the callback (similar to useEffect)
 */
export const useServerNotificationSubscription = (
    eventType: string,
    callback: (payload: any) => void,
    dependencies: React.DependencyList = []
) => {
    const { subscribe } = useServerNotifications();
    const callbackRef = useRef(callback);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    // Update callback ref when dependencies change
    useEffect(() => {
        callbackRef.current = callback;
    }, dependencies);

    // Subscribe/unsubscribe effect
    useEffect(() => {
        // Create a stable callback that uses the ref
        const stableCallback = (payload: any) => {
            callbackRef.current(payload);
        };

        // Subscribe to the event
        unsubscribeRef.current = subscribe(eventType, stableCallback);

        // Cleanup function
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [eventType, subscribe]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []);
};